import { observable, computed } from 'mobx';
import { capitalize, groupBy } from 'lodash';

import { TourStore as TourModel } from './tour';
import { hotelPageLogger } from '../../utils/logger';
import { CurrencyId } from '../models/actualization';
import { createCompositeKeyForTourPack } from '../utils/search';
import S from './index';
import { formatDay, formatDayOfWeek, formatVerbalDate, shortFormatMonth } from '../utils/format';


export class TourPackStore {

    @observable
    similarTours: TourModel[] = [];

    id: string;


    constructor(tours: TourModel[]) {
        this.similarTours = tours;
        this.id = `#${createCompositeKeyForTourPack(tours[0])}`;
    }


    @computed
    get isTourPackActualizedCompletely(): boolean {
        return this.similarTours.every(t => t.isTourActualizedCompletely);
    }

    @computed
    get isTourPackActualizationSuccess(): boolean {
        return this.similarTours.some(t => t.isDetailedActualizationSuccess);
    }

    @computed
    get isTourPackActualizationFailed(): boolean {
        return this.similarTours.every(t => t.isDetailedActualizationFailed);
    }

    @computed
    get isTourPackActualizationInProcess(): boolean {
        return this.similarTours.some(t => t.isActualizationInProcess);
    }

    @computed
    get isTourPackSold(): boolean {
        return this.similarTours.every(t => t.shouldShowAsSold);
    }

    @computed
    get isFirstTourActualizationFailed(): boolean {
        return this.firstTour && this.firstTour.isDetailedActualizationFailed;
    }

    @computed
    get mealName(): string | null {
        return this.firstTour ? this.firstTour.mealName : null;
    }

    @computed
    get mealCode(): string | null {
        return this.firstTour ? this.firstTour.mealCode : null;
    }

    @computed
    get isLinkedRoom(): boolean {
        return this.firstTour ? this.firstTour.isLinkedRoom : false;
    }

    @computed
    get roomType(): string | null {
        if (!this.firstTour) {
            return null;
        }

        if (this.firstTour.isLinkedRoom) {
            return this.firstTour.roomType;
        }

        const accommodationCode = this.firstTour.accommodationCode ? `(${this.firstTour.accommodationCode})` : '';
        const accommodationName = this.firstTour.accommodationName ? capitalize(this.firstTour.accommodationName) : '';
        const resultPhrase = [accommodationName, accommodationCode, this.firstTour.roomType].filter(Boolean);

        if (!resultPhrase.length) {
            return null;
        }

        return resultPhrase.join(' ');
    }

    @computed
    get systemRoomTypeId(): number | null {
        return this.firstTour?.systemRoomTypeId ?? null;
    }

    @computed
    get numNights(): number | null {
        return this.firstTour ? this.firstTour.nights : null;
    }

    /**
     * selkin: если был выбран тур в этом турпаке, то показываем актуальную цену выбранного тура
     * иначе показываем минимальную цену тура по турпаку
     */
    @computed
    get price(): number {
        const selectedTour = this.similarTours
            .filter(t => t.tourId === S.tours.selectedTourId)[0];

        const selectedTourBelongsToThisTourPack = !!selectedTour;

        return selectedTourBelongsToThisTourPack
            ? selectedTour.actualPrice
            : this.minActualPrice;
    }

    @computed
    get priceForNight(): number {
        return Math.ceil(this.firstTour.actualPrice / this.firstTour.nights);
    }

    @computed
    get actualizationCurrencyId(): CurrencyId {
        return this.firstTour.actualizationCurrencyId;
    }

    @computed
    get tourOperatorName(): string | null {
        return this.firstTour ? this.firstTour.operatorName : null;
    }

    @computed
    get tourOperatorId(): number | null {
        return this.firstTour ? this.firstTour.operatorId : null;
    }

    @computed
    get tourDepartureDate(): string | null {
        return (this.firstTour && this.firstTour.departDate)
            ? formatVerbalDate(this.firstTour.departDate)
            : null;
    }

    @computed
    get tourDepartureDay(): string | null {
        return (this.firstTour && this.firstTour.departDate)
            ? formatDay(this.firstTour.departDate)
            : null;
    }

    @computed
    get tourDepartureMonth(): string | null {
        return (this.firstTour && this.firstTour.departDate)
            ? shortFormatMonth(this.firstTour.departDate)
            : null;
    }

    @computed
    get tourDepartureDayOfWeek(): string | null {
        return (this.firstTour && this.firstTour.departDate)
            ? formatDayOfWeek(this.firstTour.departDate)
            : null;
    }

    @computed
    get tourArrivalDay(): string | null {
        return (this.firstTour && this.firstTour.arrivalDate)
            ? formatDay(this.firstTour.arrivalDate)
            : null;
    }

    @computed
    get tourArrivalMonth(): string | null {
        return (this.firstTour && this.firstTour.arrivalDate)
            ? shortFormatMonth(this.firstTour.arrivalDate)
            : null;
    }

    @computed
    get tourArrivalDayOfWeek(): string | null {
        return (this.firstTour && this.firstTour.arrivalDate)
            ? formatDayOfWeek(this.firstTour.arrivalDate)
            : null;
    }

    @computed
    get availableTours(): TourModel[] {
        if (!this.isTourPackActualizedCompletely || this.isTourPackSold) {
            return [];
        }

        // https://agile.sletat.ru/jiraurl/browse/SLT-2643?focusedCommentId=203247&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-203247
        return this.similarTours
            .filter(t => !t.isDuplicate && !t.shouldShowAsSold);
    }

    @computed
    get sourceId(): number | null {
        return this.firstTour ? this.firstTour.sourceId : null;
    }

    @computed
    get requestId(): number | null {
        return this.firstTour ? this.firstTour.requestId : null;
    }

    @computed
    get offerId(): number | null {
        return this.firstTour ? this.firstTour.offerId : null;
    }

    async tryActualizePack() {
        this.actualizeCurrencyRatesIfNeeded();
        await this.primaryActualizePackIfNeeded();
        await this.detailedActualizePackIfNeeded();
    }

    stopActualization(): void {
        this.similarTours.forEach(t => t.stopDetailedActualization());
    }


    @computed
    private get firstTour(): TourModel {
        return this.similarTours[0];
    }

    @computed
    private get minActualPrice(): number {
        return Math.min(...this.similarTours.map(t => t.actualPrice));
    }

    private async actualizeCurrencyRatesIfNeeded() {
        const alreadyHasCurrencyRates = this.similarTours.every(t => !t.currencyRates.isEmpty());

        if (alreadyHasCurrencyRates) {
            return;
        }

        hotelPageLogger.info(`starting getting currency rates for tour pack ${this.id}`);
        await Promise.all(
            this.similarTours.map(t => t.actualizeCurrencyRates())
        );
        hotelPageLogger.info(`getting currency rates completed for tour pack ${this.id}!`);
    }

    private async primaryActualizePackIfNeeded() {
        const alreadyPrimaryActualized = this.similarTours
            .every(t => !t.isRequiredPrimaryActualization);

        if (alreadyPrimaryActualized) {
            return;
        }

        hotelPageLogger.info(`starting primary actualizations for tour pack ${this.id}`);
        await Promise.all(
            this.similarTours.map(t => t.primaryActualize())
        );
        hotelPageLogger.info(`primary actualuzations completed for tour pack ${this.id}!`);
    }

    private async detailedActualizePackIfNeeded() {
        const alreadyDetailedActualized = this.similarTours
            .every(t => !t.isRequiredDetailedActualization);

        if (alreadyDetailedActualized) {
            return;
        }

        hotelPageLogger.info(`starting detailed actualizations for tour pack ${this.id}`);
        await Promise.all(
            this.similarTours.map(t => t.detailedActualize())
        );
        hotelPageLogger.info(`detailed actualuzations completed for tour pack ${this.id}!`);
        this.removeDuplicates();
    }

    // https://agile.sletat.ru/jiraurl/browse/SLT-2466?focusedCommentId=187733&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-187733
    private removeDuplicates(): void {
        const toursWithFlights = this.similarTours
            .filter(t => t.rawFlightsData.length > 0 && t.isDetailedActualizationSuccess);

        const groups = groupBy(toursWithFlights, t => t.rawFlightsData.join('(.)(.)'));

        const duplicates: TourModel[] = [];

        Object.keys(groups).forEach(key => {
            const tours = groups[key];

            if (tours.length <= 1) {
                return;
            }

            const bestTour = tours.reduce((best: TourModel, current: TourModel) => {
                return current.actualPrice < best.actualPrice ? current : best;
            });

            const foundDuplicates = tours.filter(t => t.tourId !== bestTour.tourId);
            hotelPageLogger.debug(`duplicates will be removed from results in favor of the best tour! duplicates and the best tour:`, foundDuplicates, bestTour);
            duplicates.push(...foundDuplicates);
        });
        duplicates.forEach(t => t.markTourAsDuplicate());
    }

}
